if (!customElements.get('custom-chart')) {
  class CustomChart extends HTMLElement {
    constructor() {
      super();
      this.chart = this.querySelector('canvas');
      if (!this.chart) return;

      this.currentIteration = parseInt(this.dataset.currentIteration);

      this.customizations = {
        title: this.dataset.title,
        vertical_title: this.dataset.verticalTitle,
        custom_text: this.dataset.customText,
        custom_text_dataset_index: this.dataset.customTextDatasetIndex,
        custom_text_data_index: this.dataset.customTextDataIndex,
        showTooltip: this.dataset.showTooltip === 'true' ? true : false,
        showRangeHigh: this.dataset.showRangeHigh === 'true' ? true : false,
        showRangeLow: this.dataset.showRangeLow === 'true' ? true : false,
        hideRangeOrigin: this.dataset.hideRangeOrigin === 'true' ? true : false,
        rangePointRadius: this.dataset.rangePointRadius,
        tension: 0.25,
        colors: {
          borderPrimary: this.dataset.lineColorPrimary,
          borderSecondary: this.dataset.lineColorSecondary,
          halfFill: this.dataset.halfFillColor,
          rangeBackground: this.dataset.predictionColor,
          dashedAxis: '#A9A29D',
          toolTipBorder: '#E6E7EB',
        },
        font: {
          family: 'Inter, sans-serif',
          weight: 500,
          size_regular: 11,
          size_heading: 14,
        },
        initial_score: this.dataset.initialScore,
        current_score: this.dataset.currentScore,
        current_iteration: parseInt(this.dataset.currentIteration),
      };

      document.addEventListener('DOMContentLoaded', this.initChart.bind(this));
    }

    initChart() {
      const currentDayCount = this.customizations.current_iteration * 15;
      const endDate = currentDayCount >= 60 ? 130 : 90;

      let initialScore = parseInt(this.customizations.initial_score);
      let currentScore = parseInt(this.customizations.current_score);
      let predictionScore = 0;
      let rangeHigh = 0;
      let rangeLow = 0;

      if (Shopify.designMode) {
        initialScore = 5;
        currentScore = 75;
      }

      const scoreRanges = [
        { min: 0, max: 119, prediction: 150, low: 130, high: 170 },
        { min: 120, max: 209, prediction: 220, low: 200, high: 240 },
        { min: 210, max: 300, prediction: 300, low: 280, high: 320 },
      ];

      const matchedRange = scoreRanges.find((range) => currentScore >= range.min && currentScore <= range.max);

      predictionScore = currentScore > 300 ? currentScore : matchedRange ? matchedRange.prediction : 0;
      rangeLow = matchedRange ? matchedRange.low : currentScore - 20;
      rangeHigh = matchedRange ? matchedRange.high : currentScore + 20;

      const maxRange = rangeHigh >= 320 ? 375 : 300;
      const showTooltip = this.customizations.showTooltip;
      const showChartTitle = this.customizations.title ? true : false;
      const chartTitle = this.customizations.title;
      const showChartVerticalTitle = this.customizations.vertical_title ? true : false;
      const chartVerticalTitle = this.customizations.vertical_title;
      const showCustomText = this.customizations.custom_text ? true : false;
      const customText = this.customizations.custom_text;
      const customTextDatasetIndex = parseInt(this.customizations.custom_text_dataset_index);
      const customTextDataIndex = parseInt(this.customizations.custom_text_data_index);

      const datasets = [
        {
          id: 'dataset-current',
          data: [initialScore, currentScore, predictionScore],
          borderWidth: 1,
          borderColor: this.customizations.colors.borderPrimary,
          pointBackgroundColor: this.customizations.colors.borderPrimary,
          pointRadius: 5,
          fill: false,
          tension: this.customizations.tension,
        },
      ];

      initialScore = this.customizations.hideRangeOrigin ? null : initialScore;

      if (this.customizations.showRangeHigh) {
        datasets.push({
          id: 'dataset-range-high',
          data: [initialScore, currentScore, rangeHigh],
          borderWidth: 1,
          borderColor: this.customizations.colors.borderSecondary,
          pointBackgroundColor: this.customizations.colors.borderSecondary,
          pointRadius: parseInt(this.customizations.rangePointRadius),
          backgroundColor: this.customizations.colors.rangeBackground,
          fill: '-1',
          tension: this.customizations.tension,
        });
      }

      if (this.customizations.showRangeLow) {
        datasets.push({
          id: 'dataset-range-low',
          data: [initialScore, currentScore, rangeLow],
          borderWidth: 1,
          borderColor: this.customizations.colors.borderSecondary,
          pointBackgroundColor: this.customizations.colors.borderSecondary,
          pointRadius: parseInt(this.customizations.rangePointRadius),
          backgroundColor: this.customizations.colors.rangeBackground,
          fill: 1,
          tension: this.customizations.tension,
        });
      }

      const labels = ['Purchase Date', 'Today', `Day ${endDate}`];

      const chart = new Chart(this.chart, {
        type: 'line',
        data: {
          labels: labels,
          datasets: datasets,
        },
        options: {
          scales: {
            y: {
              min: 0,
              max: maxRange,
              border: {
                dash: [8, 4],
                color: this.customizations.colors.dashedAxis,
              },
              ticks: {
                stepSize: 75,
              },
              beginAtZero: true,
              title: {
                display: showChartVerticalTitle,
                text: chartVerticalTitle,
                font: {
                  family: this.customizations.font.family,
                  weight: this.customizations.font.weight,
                  size: this.customizations.size_regular,
                },
              },
            },
            x: {
              border: {
                dash: [8, 4],
                color: this.customizations.colors.dashedAxis,
              },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            title: {
              display: showChartTitle,
              text: chartTitle,
              color: 'black',
              font: {
                family: this.customizations.font.family,
                weight: this.customizations.font.weight,
                size: this.customizations.size_heading,
              },
              padding: {
                bottom: 30,
              },
            },
            tooltip: {
              enabled: showTooltip,
              backgroundColor: 'white',
              bodyColor: 'black',
              borderColor: this.customizations.colors.toolTipBorder,
              borderWidth: 1,
              enabled: true,
              mode: 'index',
              intersect: false,
              yAlign: 'bottom',
              usePointStyle: true,
              callbacks: {
                title: function () {
                  return null;
                },
                label: function (tooltipItem) {
                  if (!showTooltip) return null;

                  const dataset = chart.data.datasets[tooltipItem.datasetIndex];
                  const dataValue = dataset.data[tooltipItem.dataIndex];

                  return tooltipItem.dataIndex === 2
                    ? ` ${tooltipItem.label} Prediction: ${dataValue}`
                    : ` ${tooltipItem.label}: ${dataValue}`;
                },
              },
              filter: function (tooltipItem) {
                if (!showTooltip) return null;
                return tooltipItem.datasetIndex === 0;
              },
            },
          },
        },
        plugins: [
          {
            beforeDraw: (chart) => {
              const { ctx, chartArea } = chart;
              const { left, right, top, bottom } = chartArea;
              const width = right - left;
              const midX = left + width / 2;

              ctx.fillStyle = this.customizations.colors.halfFill;
              ctx.fillRect(midX, top, right - midX, bottom - top);
            },
          },
          {
            id: 'customText',
            beforeDraw: (chart) => {
              if (!showCustomText) return;

              const ctx = chart.ctx;
              const point = chart.getDatasetMeta(customTextDatasetIndex).data[customTextDataIndex];

              const pointX = point.x - 10;
              const pointY = point.y - 20;

              ctx.font = `600 ${this.customizations.font.size_heading}px ${this.customizations.font.family}`;
              ctx.fillStyle = 'black';
              ctx.textAlign = 'right';
              ctx.textBaseline = 'top';

              ctx.fillText(customText, pointX, pointY);
            },
          },
        ],
      });

      const targetIndex = 2;
      const targetDatasetIndex = 0;

      chart.tooltip.setActiveElements([{ datasetIndex: targetDatasetIndex, index: targetIndex }], { x: 100, y: 100 });
      chart.tooltip.update();
    }
  }

  customElements.define('custom-chart', CustomChart);
}
